import { isSkipped } from './contentRoot';
import { REDACTED, isSensitiveText, redactText } from './redaction';
import { StyleView } from './types';

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

// Interactive elements are reported by the controls serializer; their text would only duplicate it.
const CONTROL_SELECTOR = 'button,a,[role="button"],[role="tab"],label,[role="menuitem"],input,select,textarea,[role="combobox"],[role="listbox"],[role="option"]';

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'BR', 'DD', 'DETAILS', 'DIV', 'DL', 'DT', 'FIELDSET',
  'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'HEADER', 'HR', 'LEGEND', 'LI', 'MAIN', 'NAV', 'OL',
  'P', 'PRE', 'SECTION', 'SUMMARY', 'UL',
]);

const HEADING_LEVELS: Record<string, number> = { H1: 1, H2: 2, H3: 3, H4: 4, H5: 5, H6: 6 };

// Every piece of text the serializers emit passes through here, so this is the one place that scrubs it.
const normalize = (text: string): string => redactText(text.replace(/\s+/g, ' ').trim());

/** Visible text of an element, flattened to one line. Control text is kept when `keepControls` is set. */
export const visibleText = (el: Element, view: StyleView, keepControls = false): string => {
  const parts: string[] = [];

  const collect = (node: Node) => {
    if (node.nodeType === TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== ELEMENT_NODE) return;

    const element = node as Element;
    if (isSkipped(element, view)) return;
    if (!keepControls && element !== el && element.matches(CONTROL_SELECTOR)) return;
    if (BLOCK_TAGS.has(element.tagName)) parts.push(' ');
    Array.from(element.childNodes).forEach(collect);
    if (BLOCK_TAGS.has(element.tagName)) parts.push(' ');
  };

  collect(el);
  return normalize(parts.join(''));
};

const escapeCell = (text: string): string => text.replace(/\|/g, '\\|');

const serializeTable = (table: HTMLTableElement, view: StyleView): string => {
  const rows = Array.from(table.rows)
    .filter((row) => !isSkipped(row, view))
    .map((row) => Array.from(row.cells)
      .filter((cell) => !isSkipped(cell, view))
      .map((cell) => escapeCell(visibleText(cell, view, true))));
  const nonEmptyRows = rows.filter((cells) => cells.some((cell) => cell !== ''));
  if (nonEmptyRows.length === 0) return '';

  const width = Math.max(...nonEmptyRows.map((cells) => cells.length));
  const pad = (cells: string[]) => cells.concat(Array(width - cells.length).fill(''));
  const toLine = (cells: string[]) => `| ${pad(cells).join(' | ')} |`;
  const [head, ...body] = nonEmptyRows;

  // A column whose header names a sensitive value (SSN, account number, ...) is redacted wholesale.
  // A header `colSpan` shifts the columns and fails open; plain tables are the supported case.
  const sensitiveColumns = head.map((cell) => isSensitiveText(cell));
  const scrub = (cells: string[]) => cells.map((cell, index) => (sensitiveColumns[index] && cell !== '' ? REDACTED : cell));

  return [toLine(head), toLine(Array(width).fill('---')), ...body.map((cells) => toLine(scrub(cells)))].join('\n');
};

/**
 * Serializes the visible headings, paragraphs, list items and tables under `root` as Markdown.
 * Inline elements are joined into the surrounding line; block elements start a new one.
 */
export const serializeTextBlocks = (root: Element, view: StyleView): string => {
  const lines: string[] = [];
  let inline: string[] = [];

  const flush = (prefix = '') => {
    const text = normalize(inline.join(''));
    inline = [];
    if (text === '') return;
    lines.push(`${prefix}${text}`);
  };

  const pushBlock = (block: string) => {
    flush();
    if (block !== '') lines.push(block);
  };

  const visit = (node: Node) => {
    if (node.nodeType === TEXT_NODE) {
      inline.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== ELEMENT_NODE) return;

    const el = node as Element;
    if (isSkipped(el, view)) return;
    if (el !== root && el.matches(CONTROL_SELECTOR)) return;

    const headingLevel = HEADING_LEVELS[el.tagName];
    if (headingLevel) {
      const text = visibleText(el, view);
      pushBlock(text === '' ? '' : `${'#'.repeat(headingLevel)} ${text}`);
      return;
    }
    if (el.tagName === 'TABLE') {
      pushBlock(serializeTable(el as HTMLTableElement, view));
      return;
    }
    if (el.tagName === 'LI') {
      flush();
      Array.from(el.childNodes).forEach(visit);
      flush('- ');
      return;
    }

    const isBlock = BLOCK_TAGS.has(el.tagName);
    if (isBlock) flush();
    Array.from(el.childNodes).forEach(visit);
    if (isBlock) flush();
  };

  visit(root);
  flush();
  return lines.join('\n');
};
